import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { FaShieldAlt, FaLightbulb } from "react-icons/fa";
import heroImg from "../assets/elementor-widget.webp";
import { springSmooth, springBouncy, EXPO_OUT } from "../utils/animations";

export default function AboutUs() {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const inView = useInView(headingRef, { once: true, margin: "-100px" });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={sectionRef} id="about" className="relative py-20 lg:py-28 overflow-hidden bg-white dark:bg-main">
      {/* Subtle background texture with scroll parallax */}
      <motion.div
        className="absolute inset-0 bg-cover bg-center opacity-[0.03] dark:opacity-[0.05]"
        style={{ backgroundImage: `url(${heroImg})`, y: bgY }}
      />

      {/* Decorative orbs */}
      <div className="absolute top-1/2 start-0 w-80 h-80 bg-highlight/5 rounded-full blur-[80px] -translate-y-1/2 animate-float-orb" />
      <div className="absolute bottom-0 end-1/4 w-64 h-64 bg-highlight/3 rounded-full blur-[60px] animate-float-orb-delayed" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div ref={headingRef} className="text-center mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
            animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.7, ease: EXPO_OUT }}
            className="text-3xl md:text-4xl font-bold text-main dark:text-white font-syne mb-4"
          >
            {t("about.title")}
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={inView ? { scaleX: 1, opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3, ease: EXPO_OUT }}
            className="w-24 h-1 bg-gradient-to-r from-transparent via-highlight to-transparent mx-auto rounded-full"
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          {/* Text column — slide + blur in from left */}
          <motion.div
            initial={{ opacity: 0, x: -80, filter: "blur(8px)" }}
            whileInView={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ ...springSmooth }}
            className="space-y-6"
          >
            <div className="flex gap-4">
              <motion.div
                whileHover={{ rotate: 15, scale: 1.15 }}
                transition={springBouncy}
                className="flex-shrink-0 w-12 h-12 bg-highlight/10 border border-highlight/20 rounded-xl flex items-center justify-center mt-1 hover:shadow-[0_0_20px_rgba(201,162,39,0.15)] transition-shadow"
              >
                <FaShieldAlt className="text-highlight text-lg" />
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-main/80 dark:text-white/80 font-cairo leading-relaxed text-base"
              >
                {t("about.p1")}
              </motion.p>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-gray-400 dark:text-white/40 font-cairo leading-relaxed text-base ps-16"
            >
              {t("about.p2")}
            </motion.p>
          </motion.div>

          {/* Innovation column — slide from right with 3D hover */}
          <motion.div
            initial={{ opacity: 0, x: 80, filter: "blur(8px)" }}
            whileInView={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ ...springSmooth, delay: 0.15 }}
            whileHover={{ y: -6, scale: 1.01 }}
            className="group bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 rounded-2xl p-8 shadow-card dark:shadow-card-dark hover:border-highlight/20 hover:shadow-card-hover dark:hover:shadow-card-dark-hover transition-all duration-500 overflow-hidden relative gradient-border"
          >
            {/* Shimmer on hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-shimmer rounded-2xl pointer-events-none" />

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-5">
                <motion.div
                  animate={{ rotate: [0, 8, -8, 4, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="w-12 h-12 bg-highlight/10 border border-highlight/20 rounded-xl flex items-center justify-center"
                >
                  <FaLightbulb className="text-highlight text-lg" />
                </motion.div>
                <h3 className="text-xl font-bold text-highlight font-cairo">
                  {t("about.innovationTitle")}
                </h3>
              </div>
              <p className="text-gray-400 dark:text-white/40 font-cairo leading-relaxed text-base">
                {t("about.p3")}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
